// This is the previous LoginForm moved to its own page
import React, { useState } from "react";
import { useAuth, API_BASE as BACKEND_URL } from "./AuthContext";
import { useNavigate } from "react-router-dom";
import PublicHeader from "./PublicHeader";

const LoginClassic = ({ onPendingConfirmation, onForgotPassword, onGoToRegister }) => {

  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotPending, setForgotPending] = useState(false);
  const [forgotMsg, setForgotMsg] = useState("");
  const [resendPending, setResendPending] = useState(false);
  const [resendMsg, setResendMsg] = useState("");
  
  const isLikelyEmail = (val) => /\S+@\S+\.\S+/.test(val);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(""); setForgotMsg(""); setResendMsg("");
    setPending(true);
    const loginResult = await login(email, password, onPendingConfirmation);
    setPending(false);

    if (typeof loginResult === "string" && loginResult) {
      setError(loginResult);
      setFailedAttempts((fa) => fa + 1);
    } else if (loginResult === true) {
      setFailedAttempts(0);
      try {
        const params = new URLSearchParams(window.location.search || "");
        const qt = params.get("quote");
        if (qt) {
          navigate(`/?quote=${encodeURIComponent(qt)}`);
        } else {
          navigate("/");
        }
      } catch (_) {
        navigate("/");
      }
    }
  };

  const handleResend = async () => {
    if (!email) { setResendMsg("Please enter your e-mail address."); return; }
    if (!isLikelyEmail(email)) { setResendMsg("Please enter a valid e-mail address."); return; }
    setResendPending(true); setResendMsg("");
    try {
      const res = await fetch(`${BACKEND_URL}/resend-confirmation`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (res.ok) {
        setResendMsg("Confirmation e-mail sent!");
        if (onPendingConfirmation) onPendingConfirmation(email);
      } else {
        const data = await res.json().catch(() => ({}));
        setResendMsg(data.detail || "Failed to resend confirmation code.");
      }
    } catch { setResendMsg("Network error."); }
    setResendPending(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!email) { setForgotMsg("Please enter your e-mail address first."); return; }
    if (!isLikelyEmail(email)) { setForgotMsg("Please enter a valid e-mail address."); return; }
    setForgotPending(true); setForgotMsg("");
    try {
      const res = await fetch(`${BACKEND_URL}/forgot-password`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (res.ok) {
        setForgotMsg("If your account exists and is confirmed, a reset link will be sent.");
      } else {
        const data = await res.json().catch(() => ({}));
        setForgotMsg(data.message || data.detail || "Failed to send reset link.");
      }
    } catch { setForgotMsg("Network error."); }
    setForgotPending(false);
  };

  const isUnconfirmedError = error === "Email not confirmed. Please check your inbox.";
  const isSuccessText = (txt) => typeof txt === "string" && /sent|link|success|confirmed/i.test(txt);

  return (
    <div className="mx-auto px-4">
	  <PublicHeader />

      <div className="max-w-lg mx-auto p-6 bg-white rounded-2xl">
        <h2 data-testid="login-title" className="text-2xl font-bold mb-3 text-center">Login</h2>

        <form onSubmit={(e) => e.preventDefault()} className="flex flex-col gap-4">
          <input
            data-testid="login-email"
            type="email"
            placeholder="E-mail Address"
            className="p-2 border rounded"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="username"
            required
            aria-label="E-mail Address for reset"
          />

          <div className="flex items-center gap-2">
            <input
              data-testid="login-password"
              type={showPassword ? "text" : "password"}
              placeholder="Password"
              className="p-2 border rounded flex-1"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
              aria-label="Password"
            />
            <button
              data-testid="login-show-password"
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="px-2 py-1 border rounded flex items-center justify-center"
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              <img
                src={showPassword ? "/show_password.png" : "/hide_password.png"}
                alt={showPassword ? "Hide password" : "Show password"}
                className="h-4 w-4"
              />
            </button>
          </div>

          <button
            data-testid="login-submit"
            type="button"
			onClick={handleLogin}
            className="text-white px-4 py-2 rounded w-full"
            style={{ backgroundColor: "#00a7e1" }}
            disabled={pending || forgotPending}
          >
            {pending ? "Working..." : "Login"}
          </button>

          {/* Error & unconfirmed handling */}
          {isUnconfirmedError ? (
            <div className="text-center">
              <div data-testid="login-unconfirmed-message" className="text-red-700" aria-live="polite">
                Email not confirmed. Please check your inbox.&nbsp;
                <button
                  data-testid="login-resend-confirmation"
                  type="button"
                  className="underline"
                  style={{ color: "#00a7e1" }}
                  onClick={handleResend}
                  disabled={resendPending || !email || !isLikelyEmail(email)}
                  aria-disabled={resendPending || !email || !isLikelyEmail(email)}
                >
                  Resend code?
                </button>
              </div>
              {resendMsg && (
                <div
                  data-testid="login-resend-message"
                  className={isSuccessText(resendMsg) ? "mt-1" : "text-red-700 mt-1"}
                  style={isSuccessText(resendMsg) ? { color: "#94c83d" } : undefined}
                  aria-live="polite"
                >
                  {resendMsg}
                </div>
              )}
            </div>
          ) : (
            error && (
              <div
                data-testid="login-error"
                className="text-red-700 text-center"
                aria-live="polite"
              >
                {error}
              </div>
            )
          )}

          {/* Forgot Password */}
          <div className="text-center mt-2">
            {!showForgot ? (
              <button
                data-testid="login-forgot-password"
                type="button"
                className="underline"
                style={{ color: "#00a7e1" }}
                onClick={() => setShowForgot(true)}
                disabled={pending || forgotPending}
              >
                Forgot password?
              </button>

            ) : (

              <div className="flex flex-col items-center gap-2">
                <div data-testid="login-forgot-instructions" className="mb-2 text-base">We&apos;ll send a password reset link to your email address.</div>
                <div className="w-full max-w-xs">
                  <input
                    data-testid="forgot-password-email"
                    type="email"
                    placeholder="E-mail Address"
                    className="p-2 border rounded w-full"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="username"
                    aria-label="E-mail Address for reset"
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !forgotPending && isLikelyEmail(email)) {
                        handleForgotPassword(e);
                      }
                    }}
                  />

				  <button
                    data-testid="forgot-password-submit"
                    type="button"
                    className="text-white px-3 py-2 rounded w-full"
                    style={{ backgroundColor: "#00a7e1" }}
                    onClick={handleForgotPassword}
                    disabled={forgotPending || !email || !isLikelyEmail(email)}
                    aria-disabled={forgotPending || !email || !isLikelyEmail(email)}
                  >
                    {forgotPending ? "Sending..." : "Send Reset Link"}
                  </button>
                </div>
                <button
                  data-testid="forgot-password-cancel"
                  type="button"
                  className="underline"
                  style={{ color: "#00a7e1" }}
                  onClick={() => { setShowForgot(false); setForgotMsg(""); }}
                  disabled={forgotPending}
                >
                  Cancel
                </button>
                {forgotMsg && (
                  <div
                    data-testid="login-forgot-message"
                    className={isSuccessText(forgotMsg) ? "mt-1" : "text-red-700 mt-1"}
                    style={isSuccessText(forgotMsg) ? { color: "#94c83d" } : undefined}
                    aria-live="polite"
                  >
                    {forgotMsg}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* New-user signup callout */}
          <div className="mt-4 mb-2">
            <div className="mx-auto max-w-lg rounded-xl border border-blue-200 bg-blue-50/60 p-4 shadow-sm">
              <div className="text-center space-y-1">
                <h2 className="text-lg font-bold text-gray-900">
                  Start Your Free Trial
                </h2>
                <button
                  data-testid="login-create-account"
                  type="button"
                  className="underline font-semibold text-[#00a7e1] hover:text-[#008fc2] focus:outline-none focus:underline"
                  onClick={() => navigate("/register")}
                >
                  <span className="block whitespace-nowrap">Create your free account and receive 25 AI credits</span>
                  <span className="block whitespace-nowrap">to experience ZabFlow AI.</span>
                </button>
                <p className="text-sm text-gray-600">
                  No obligations. No credit card required.
                </p>
              </div>
            </div>
          </div>
        </form>
      </div>

      {/* Wide yellow warning */}
      <div className="mt-8 mx-auto w-full max-w-screen-lg">
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 text-sm text-gray-800 leading-relaxed">
          <p>
            <b>AI workflows are designed for fast turnaround and internal or draft translations.</b> They can help when
            time constraints make it difficult to obtain a professional translation. While the goal is to produce
            understandable and reasonably formatted results, <b>translation accuracy, linguistic quality, and file formatting are not guaranteed</b>.
          </p>
          <p className="mt-3">
            For client-ready, publication-quality, or fully formatted translations, we strongly recommend {" "}
            <a href="/workflow-builder" className="underline" style={{ color: "#00a7e1" }}>
              building a custom translation workflow
            </a>{" "}
            with professional review and formatting or using{" "}
			<a
              href="https://zabtranslation.com/request-a-free-quote.html"
              target="_blank"
              rel="noopener noreferrer"
              className="underline"
              style={{ color: "#00a7e1" }}
            >
			our professional translation services.
			</a>{" "}
          </p>
          <p className="mt-3 font-bold">
            For expert assistance, please contact{" "}
            <a href="mailto:request@zabtranslation.com" className="underline" style={{ color: "#00a7e1" }}>
              request@zabtranslation.com
            </a>{" "}
            or visit{" "}
            <a
              href="https://www.zabtranslation.com"
              target="_blank"
              rel="noopener noreferrer"
              className="underline"
              style={{ color: "#00a7e1" }}
            >
              www.zabtranslation.com
            </a>{" "}
            for more information.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginClassic;